/**
 * Pure view-selection logic for HandoffTimeoutPanel's small view machine
 * (options → message | callback → submitted — see
 * src/components/assistant/handoff-timeout-panel.tsx). Factored out as a
 * plain function — no React — so the transition table is unit-tested
 * without mounting the panel (same precedent as connectionReducer /
 * send-routing.ts / useHandoff.ts's extracted phase-transition functions:
 * see useHandoff.ts's header comment for why this codebase prefers that
 * split).
 *
 * Every transition is CONTEXT-FREE: the target view depends only on the
 * action fired, never on which view is currently showing. CHOOSE_MESSAGE
 * always lands on MESSAGE, BACK always returns to OPTIONS (from either
 * sub-view), and SUBMITTED is terminal. So this is a lookup table, not a
 * state-dependent reducer — no `current` parameter needed.
 */

export const HandoffTimeoutView = {
  OPTIONS: 'options',
  MESSAGE: 'message',
  CALLBACK: 'callback',
  SUBMITTED: 'submitted',
} as const;
export type HandoffTimeoutView =
  (typeof HandoffTimeoutView)[keyof typeof HandoffTimeoutView];

export const HandoffTimeoutAction = {
  CHOOSE_MESSAGE: 'choose_message',
  CHOOSE_CALLBACK: 'choose_callback',
  BACK: 'back',
  SUBMITTED: 'submitted',
} as const;
export type HandoffTimeoutAction =
  (typeof HandoffTimeoutAction)[keyof typeof HandoffTimeoutAction];

/** Returns the view HandoffTimeoutPanel should show after `action` fires. */
export function nextTimeoutView(
  action: HandoffTimeoutAction
): HandoffTimeoutView {
  switch (action) {
    case HandoffTimeoutAction.CHOOSE_MESSAGE:
      return HandoffTimeoutView.MESSAGE;
    case HandoffTimeoutAction.CHOOSE_CALLBACK:
      return HandoffTimeoutView.CALLBACK;
    case HandoffTimeoutAction.BACK:
      return HandoffTimeoutView.OPTIONS;
    case HandoffTimeoutAction.SUBMITTED:
      return HandoffTimeoutView.SUBMITTED;
    default:
      // Unreachable — exhaustive over HandoffTimeoutAction. Satisfies
      // noImplicitReturns-style tooling without a `never` cast this repo's
      // (non-strict) tsconfig doesn't require.
      return HandoffTimeoutView.OPTIONS;
  }
}
